import { FileUploadMetaDataExtractionDto } from '@asset-metrix/portal-gateway-client-js';
import { FileSelection, Options } from '@asset-metrix/portal-ui';
import { JsonSchema } from '@sagold/react-json-editor';

import {
  METADATA_EXTRACTION_FROM_FILE_NAME_VALUE,
  METADATA_EXTRACTION_FROM_USER_VALUE,
  METADATA_EXTRACTION_TYPE,
} from './constants';

export type ComputeFileInitialDataParams = {
  tableModalValues?: { [p: string]: Options };
  fileNameMetadataExtractionConfig?: FileUploadMetaDataExtractionDto;
  editorInitialData: Record<string, unknown>;
  extractionMethod: METADATA_EXTRACTION_TYPE;
  file: FileSelection;
  fileMetadata?: Record<string, unknown>;
  jsonSchema: JsonSchema;
};

function mergeHiddenDependencyValues(
  result: Record<string, unknown>,
  jsonSchema: JsonSchema,
  editorInitialData: Record<string, unknown>
): Record<string, unknown> {
  const mergedResult = { ...result };
  if (!jsonSchema.properties) {
    return mergedResult;
  }
  for (const [key, property] of Object.entries(jsonSchema.properties)) {
    if (
      typeof property === 'object' &&
      property.options?.hidden === true &&
      editorInitialData[key] !== undefined &&
      mergedResult[key] === undefined
    ) {
      mergedResult[key] = editorInitialData[key];
    }
  }
  return mergedResult;
}

/**
 * Computes the initial data for a file's metadata editor.
 * For from_user it will return editorInitialData.
 * For from_file name it will get data from from fileState.value.
 * For from_ai it will return empty object as the initial data is calculated ouside this helper.
 *
 * Hidden dependency fields (used by allOf if/then) are always populated
 * from step 1's editorInitialData so conditional validation works in step 2.
 */
export function computeFileInitialData({
  tableModalValues,
  fileNameMetadataExtractionConfig,
  editorInitialData,
  extractionMethod,
  file,
  fileMetadata,
  jsonSchema,
}: ComputeFileInitialDataParams): Record<string, unknown> {
  // fileMetadata (perFileMetaData) will be set once user changes any values in the jsonEditor
  // if fileMetadata is set just return it as initial data, no need to get metadata from file.
  if (fileMetadata !== undefined) {
    return mergeHiddenDependencyValues(
      fileMetadata,
      jsonSchema,
      editorInitialData
    );
  }

  // get initial data for from_user extraction-method
  if (
    extractionMethod === METADATA_EXTRACTION_FROM_USER_VALUE &&
    typeof editorInitialData === 'object' &&
    Object.entries(editorInitialData).length > 0
  ) {
    const initialData: Record<string, unknown> = {};
    if (jsonSchema.properties) {
      for (const key of Object.keys(jsonSchema.properties)) {
        initialData[key] = editorInitialData[key];
      }
    }
    return initialData;
  }

  if (extractionMethod === METADATA_EXTRACTION_FROM_FILE_NAME_VALUE) {
    // get initial data for from_file extraction-method
    if (!fileNameMetadataExtractionConfig) {
      return mergeHiddenDependencyValues({}, jsonSchema, editorInitialData);
    }

    const { key, source = 'label' } = fileNameMetadataExtractionConfig;
    const { value } = file.extractedMetaData || {};

    if (
      !tableModalValues ||
      !jsonSchema.properties ||
      typeof jsonSchema.properties[key] !== 'object' ||
      !jsonSchema.properties[key].conditionalFilter?.dataProperty
    ) {
      return mergeHiddenDependencyValues({}, jsonSchema, editorInitialData);
    }

    const data: Options =
      tableModalValues[
        jsonSchema.properties[key].conditionalFilter?.dataProperty
      ];
    if (!data) {
      return mergeHiddenDependencyValues({}, jsonSchema, editorInitialData);
    }

    if (typeof value === 'string') {
      let index = -1;
      if (source === 'label') {
        index = data.labels.indexOf(value);
      } else if (source === 'id') {
        index = data.values.indexOf(value);
      }
      if (index !== -1) {
        return mergeHiddenDependencyValues(
          { [key]: [data.values[index]] },
          jsonSchema,
          editorInitialData
        );
      }
    }
  }

  return mergeHiddenDependencyValues({}, jsonSchema, editorInitialData);
}
